/**
 * Mức ưu tiên thông báo theo người nhận (anh Tiến 21/10/2026: "quá nhiều luồng đổ tin về admin, trưởng phòng, trưởng bộ phận";
 * "tài sản, doanh thu, con người là ưu tiên hàng đầu"). Xem docs/phases/X-THONG-BAO-UU-TIEN.md.
 *
 *  - Khẩn (urgent): đang chặn tiền, hàng, xe, người, hệ thống → chuông + đẩy điện thoại, không tắt được.
 *  - Trong ngày (today): việc của mình, làm trong ngày → chuông + đẩy theo nhóm đã bật.
 *  - Thông tin (info): biết là được → tab Bản tin, không số đỏ, không đẩy.
 *
 * Mỗi người nhận có một "vai" với thông báo đó:
 *  - doer: người làm việc (hoặc người duy nhất nhận) → mức gốc.
 *  - head: trưởng bộ phận – cùng khối với người nhận khác nhưng cấp cao hơn → chỉ Khẩn vào ngay; còn lại vào Bản tin, leo thang khi quá hạn.
 *  - exec: Giám đốc / Quản trị (vai trò cấp ≥ 200) khi còn người khác nhận → vào Bản tin, không leo thang (anh Tiến 30/10/2026:
 *    "Trưởng phòng duyệt hết, giám đốc giám sát", "bản tin 9:00 và 15h30"): GĐ xem 2 bản tin giám sát/ngày. Riêng nhóm Hệ thống giữ mức gốc.
 */
export const NOTIFY_LEVELS = ['urgent', 'today', 'info'] as const;
export type NotifyLevel = (typeof NOTIFY_LEVELS)[number];
export const NOTIFY_LEVEL_LABELS: Record<NotifyLevel, string> = { urgent: 'Khẩn', today: 'Trong ngày', info: 'Thông tin' };

export const NOTIFY_AUDIENCES = ['doer', 'head', 'exec'] as const;
export type NotifyAudience = (typeof NOTIFY_AUDIENCES)[number];

export const NOTIFY_AREAS = ['tai_san', 'doanh_thu', 'con_nguoi', 'van_hanh', 'he_thong', 'thong_tin'] as const;
export type NotifyArea = (typeof NOTIFY_AREAS)[number];
export const NOTIFY_AREA_LABELS: Record<NotifyArea, string> = {
  tai_san: 'Tài sản',
  doanh_thu: 'Doanh thu',
  con_nguoi: 'Con người',
  van_hanh: 'Vận hành',
  he_thong: 'Hệ thống',
  thong_tin: 'Thông tin chung',
};
/** Nhóm ưu tiên hàng đầu: việc Trong ngày còn treo thì leo thang tới Giám đốc. */
export const TOP_AREAS: readonly NotifyArea[] = ['tai_san', 'doanh_thu', 'con_nguoi'];

/** Giờ leo thang (phút, tính từ lúc tạo, khi chưa ai trong số người nhận mở thông báo). */
/** Giờ VN gửi tóm tắt Bản tin cuối ngày cho trưởng bộ phận / GĐ (Thứ 2 → Thứ 7); khởi động lại trước NOTIFY_DIGEST_UNTIL thì gửi bù. */
export const NOTIFY_DIGEST_AT = '17:30';
export const NOTIFY_DIGEST_UNTIL = '22:00';
/** Giờ VN gửi bản tin giám sát cho Giám đốc / Quản trị (Thứ 2 → Thứ 7), mỗi bản gom tin từ bản trước tới lúc gửi. */
export const EXEC_DIGEST_AT = ['09:00', '15:30'] as const;

export const ESCALATE_MINUTES = { headToday: 120, execUrgent: 30, execTopToday: 240 } as const;

interface Policy {
  level: NotifyLevel;
  area: NotifyArea;
  /** Trưởng bộ phận nhận cùng mức với người làm (tin tốt về doanh thu, việc duyệt). */
  headsSame?: boolean;
}

const P = (level: NotifyLevel, area: NotifyArea, headsSame?: boolean): Policy => ({ level, area, headsSame });

const POLICY: Record<string, Policy> = {
  // ---- Tài sản: hàng, xe, kho
  DELIVERY_PICK_SHORT: P('urgent', 'tai_san'),
  FLEET_GPS_LONG_STOP: P('urgent', 'tai_san'),
  FLEET_TRIP_OVERNIGHT: P('urgent', 'tai_san'),
  FLEET_TRIP_FLAGGED: P('urgent', 'tai_san'),
  FLEET_FUEL_VARIANCE: P('urgent', 'tai_san'),
  FLEET_CHECKLIST_LOCKED: P('urgent', 'tai_san'),
  FLEET_SERVICE_REQUEST: P('today', 'tai_san'),
  FLEET_TIRE_ALERT: P('today', 'tai_san'),
  FLEET_CONDITION: P('today', 'tai_san'),
  FLEET_EXPIRING: P('today', 'tai_san'),
  FLEET_MAINTENANCE_DUE: P('today', 'tai_san'),
  // DEC-430: việc giao tài xế (nhắc việc khẩn gửi priority URGENT → Khẩn), tài xế báo xong
  FLEET_TASK_ASSIGNED: P('today', 'tai_san'),
  FLEET_TASK_DONE: P('today', 'tai_san'),
  FLEET_RETURN_REMINDER: P('today', 'tai_san'),
  FLEET_CRANE_OPEN: P('urgent', 'tai_san'),
  FLEET_AUTO_RETURN: P('today', 'tai_san'),
  STOCK_TRANSFER_IN: P('today', 'tai_san'),
  STOCK_COUNT_REQUESTED: P('today', 'tai_san'),
  STOCK_COUNT_PROPOSED: P('today', 'tai_san'),
  STOCK_COUNT_SUBMITTED: P('today', 'tai_san', true),
  STOCK_COUNT_APPROVED: P('info', 'tai_san'),
  FLEET_FUEL_PENDING: P('today', 'tai_san', true),
  FLEET_EXPENSE_PENDING: P('today', 'tai_san', true),
  FLEET_FUEL_PAY: P('today', 'tai_san'),
  FLEET_CLOSEOUT_DUE: P('today', 'tai_san'),
  FLEET_FUEL_WEEKLY: P('info', 'tai_san'),
  FLEET_MONTH_REPORT: P('today', 'tai_san'),
  FLEET_ODO_ANOMALY: P('today', 'tai_san'),
  FLEET_FINE_CHECK: P('today', 'tai_san'),
  FLEET_WEEKLY_CHECK: P('today', 'tai_san'),
  FLEET_YARD_CHECK: P('today', 'tai_san'),
  FLEET_YARD_REPORT: P('today', 'tai_san'),
  FLEET_FINE: P('today', 'tai_san'),
  FLEET_GPS_REPORT: P('info', 'tai_san'),

  // ---- Doanh thu: đơn, giao, tiền, khách
  PURCHASE_SHORTAGE: P('urgent', 'doanh_thu'),
  PURCHASE_RFQ_READY: P('today', 'doanh_thu'),
  SUPPLIER_PORTAL_MESSAGE: P('today', 'doanh_thu'),
  DELIVERY_ENOUGH_NEEDED: P('urgent', 'doanh_thu'),
  DELIVERY_OVERDUE: P('today', 'doanh_thu'),
  CONTROL_CHECK_MISMATCH: P('urgent', 'doanh_thu'),
  COMPLAINT_OVERDUE: P('urgent', 'doanh_thu'),
  ASIA_SO1_CHANGED: P('today', 'doanh_thu'),
  COMPLAINT_ASSIGNED: P('today', 'doanh_thu'), // khiếu nại khẩn có priority URGENT → Khẩn
  CONTROL_CHECK_REQUESTED: P('today', 'doanh_thu'),
  TEMP_CODE_NEEDED: P('today', 'doanh_thu'),
  APPROVAL_REQUESTED: P('today', 'doanh_thu', true),
  QUOTE_REF_COST: P('today', 'doanh_thu', true),
  CTV_COMMISSION: P('today', 'doanh_thu', true),
  APPROVAL_APPROVED: P('today', 'doanh_thu'),
  // P23 bộ máy duyệt chung: việc chờ duyệt (người duyệt), kết quả (người gửi), quá hạn báo cấp trên
  WORKFLOW_TASK: P('today', 'van_hanh'),
  WORKFLOW_RESULT: P('today', 'van_hanh'),
  WORKFLOW_OVERDUE: P('today', 'van_hanh'),
  ESIGN_TASK: P('today', 'van_hanh'),
  ESIGN_RESULT: P('today', 'van_hanh'),
  APPROVAL_REJECTED: P('today', 'doanh_thu'),
  QUOTE_ACCEPTED: P('today', 'doanh_thu', true),
  OPPORTUNITY_WON: P('today', 'doanh_thu', true),
  OPPORTUNITY_ASSIGNED: P('today', 'doanh_thu'),
  CONTRACT_SIGNED: P('today', 'doanh_thu', true),
  POSTER_ORDER: P('today', 'doanh_thu'),
  WEB_QUOTE_REQUEST: P('today', 'doanh_thu'),
  LEAD_ASSIGNED: P('today', 'doanh_thu'),
  SITE_REPORTED: P('today', 'doanh_thu'),
  WEB_CHAT_NEW: P('today', 'doanh_thu'),
  DEBT_CONFIRMATION_RESPONDED: P('today', 'doanh_thu'),
  QUOTE_EXPIRING: P('today', 'doanh_thu'),
  FOLLOW_UP_DUE: P('today', 'doanh_thu'),
  QUOTE_SENT: P('info', 'doanh_thu'),
  QUOTE_LOST: P('info', 'doanh_thu'),
  QUOTE_EXTENDED: P('info', 'doanh_thu'),
  OPPORTUNITY_CREATED: P('info', 'doanh_thu'),
  OPPORTUNITY_LOST: P('info', 'doanh_thu'),
  COMPLAINT_RESOLVED: P('info', 'doanh_thu'),
  SITE_UPDATED: P('info', 'doanh_thu'),
  PRICE_LIST_CHANGED: P('info', 'doanh_thu'),

  // ---- Con người
  HR_VIOLATION: P('today', 'con_nguoi'),
  HR_PRIVATE_CHANGED: P('today', 'con_nguoi'),
  HR_HEALTH_DOC_EXPIRING: P('today', 'con_nguoi'),
  HR_CHECKIN_MISSING: P('today', 'con_nguoi'),
  HR_LEAVE_REQUESTED: P('today', 'con_nguoi', true),
  HR_ADVANCE_REQUESTED: P('today', 'con_nguoi', true),
  HR_LEAVE_DECIDED: P('today', 'con_nguoi'),
  HR_ADVANCE_DECIDED: P('today', 'con_nguoi'),
  HR_CONTRACT_EXPIRING: P('today', 'con_nguoi'),
  HR_PROBATION_ENDING: P('today', 'con_nguoi'),
  HR_CONTRACT_SIGN: P('today', 'con_nguoi'),
  HR_WORK_ORDER: P('today', 'con_nguoi'),
  HR_TIMESHEET_CONFIRM: P('today', 'con_nguoi'),
  HR_PAYOUT_RECON: P('today', 'con_nguoi'),
  HR_PIECE_PAYMENT: P('today', 'con_nguoi'),
  SITE_PROGRESS: P('today', 'con_nguoi'),
  // DEC-580: hạn hỏi làm rõ, đóng thầu, bảo lãnh dự thầu (người phụ trách hồ sơ)
  TENDER_DEADLINE: P('today', 'doanh_thu'),
  WEB_POST: P('today', 'thong_tin'),
  HR_PAYROLL_RECOVERY: P('today', 'con_nguoi'),
  HR_KPI: P('today', 'con_nguoi'),
  HR_TRAINING: P('info', 'con_nguoi'),
  HR_POLICY_PUBLISHED: P('info', 'con_nguoi'),

  // ---- Vận hành: soạn, gắn xe, giao
  DELIVERY_PICK: P('today', 'van_hanh'),
  DELIVERY_PRINT_REQUEST: P('today', 'van_hanh'),
  ORDER_INCOMING_GOODS: P('today', 'van_hanh'),
  DRIVER_DAY_BRIEF: P('today', 'van_hanh'),
  DELIVERY_GOODS_ARRIVED: P('today', 'van_hanh'),
  DELIVERY_UNASSIGNED: P('today', 'van_hanh'),
  DELIVERY_CONTACT_MISSING: P('today', 'van_hanh'),
  SO3_DOCS_INCOMPLETE: P('today', 'van_hanh'),
  DELIVERY_ENOUGH_CONFIRMED: P('today', 'van_hanh'),
  DELIVERY_ASSIGNED: P('today', 'van_hanh'),
  DELIVERY_VEHICLE_CHANGED: P('today', 'van_hanh'),
  DELIVERY_TRIP_STARTED: P('today', 'van_hanh'),
  FLEET_PICKUP_ASSIGNED: P('today', 'van_hanh'),
  DELIVERY_AUTO_ASSIGNED: P('info', 'van_hanh'),
  DELIVERY_DISPATCHED: P('info', 'van_hanh'),
  FLEET_PICKUP_PROGRESS: P('info', 'van_hanh'),
  // DEC-800 hàng lấy về: chờ chọn xuống kho / để trên xe / sang xe, kho kiểm + xuống hàng
  FLEET_PICKUP_CARGO: P('urgent', 'tai_san'),
  // DEC-412: ví Lalamove (tiền công ty) sắp hết → người giữ ví nạp trong ngày
  CARRIER_WALLET_LOW: P('today', 'tai_san'),
  // DEC-475: Lalamove không có tài xế → người đặt / điều phối xoay xe khác ngay
  CARRIER_NO_DRIVER: P('urgent', 'van_hanh'),

  // ---- Hệ thống: Giám đốc / Quản trị nhận đúng mức gốc
  SYSTEM_EVENT_FAILED: P('urgent', 'he_thong'),
  SYSTEM_DISK_LOW: P('urgent', 'he_thong'),
  ASIA_SYNC_FAILED: P('urgent', 'he_thong'),
  AI_BUDGET: P('urgent', 'he_thong'),
  ASIA_SYNC_ERRORS: P('today', 'he_thong'),

  // ---- Thông tin chung
  DAILY_SUMMARY: P('info', 'thong_tin'),
  NOTIFY_DIGEST: P('info', 'thong_tin'),
  // 30/10/2026: bản tin giám sát 09:00 / 15:30 của GĐ → chuông (2 lần/ngày thay cho chuông lẻ)
  EXEC_DIGEST: P('today', 'thong_tin'),
  FEED_POST: P('info', 'thong_tin'),
  FEED_COMMENT: P('info', 'thong_tin'),
  FEED_REACTION: P('info', 'thong_tin'),
  FEED_BIRTHDAY: P('info', 'thong_tin'),
  LUCKY_WHEEL_WIN: P('info', 'thong_tin'),
};

const DEFAULT_POLICY: Policy = { level: 'today', area: 'van_hanh' };

export function notifyPolicyOf(notificationType: string): Required<Policy> {
  const p = POLICY[notificationType] ?? DEFAULT_POLICY;
  return { level: p.level, area: p.area, headsSame: !!p.headsSame };
}

/** Mức gốc của thông báo: theo loại; thông báo gắn priority URGENT (vd khiếu nại khẩn) luôn Khẩn. */
export function baseLevelOf(notificationType: string, priority?: string | null): NotifyLevel {
  return priority === 'URGENT' ? 'urgent' : notifyPolicyOf(notificationType).level;
}

/** Mức thông báo của một người nhận theo vai (doer / head / exec). */
export function levelFor(notificationType: string, base: NotifyLevel, audience: NotifyAudience): NotifyLevel {
  if (audience === 'doer' || base === 'info') return base;
  const p = notifyPolicyOf(notificationType);
  if (audience === 'head') return base === 'urgent' || p.headsSame ? base : 'info';
  return p.area === 'he_thong' ? base : 'info';
}

/**
 * Sau bao nhiêu phút thì nâng dòng Bản tin của người nhận lên chuông (leo thang), hoặc null nếu không leo thang.
 *  - Trưởng bộ phận: việc Trong ngày treo 2 giờ.
 *  - Giám đốc / Quản trị: không leo thang (30/10/2026, Trưởng phòng xử lý hết); việc Khẩn chưa ai mở hiện trong bản tin giám sát.
 */
export function escalateAfterMinutes(notificationType: string, base: NotifyLevel, audience: NotifyAudience): number | null {
  if (audience !== 'head' || base === 'info' || levelFor(notificationType, base, audience) === base) return null;
  return ESCALATE_MINUTES.headToday;
}

/** Danh sách loại đã khai báo (dùng để kiểm đủ loại ở test). */
export const NOTIFY_POLICY_TYPES = Object.keys(POLICY);
